import React, { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocalizedStrings } from '../../Localization';
import { BreadcrumbProps, Button, Icon, NumberDisplay, Tooltip } from '@aidkitorg/component-library';
import { useNotifications } from '../../Contexts/NotificationsContext';
import { NotificationsDataTable } from './components/NotificationsDataTable';
import { generateTimestampString } from '../../utils/adminPanel.utils';
import { AdminPanelPage } from '../AdminPanelPage';
import { usePost } from '../../API';
import InterfaceContext from '../../Context';

export const NotificationsPage = () => {
  const L = useLocalizedStrings()
  const context = useContext(InterfaceContext);

  const {
    fetchNotificationsOverview,
    enabledNotifications,
    notifications,
    notificationsDataTimestamp,

    isNotificationsLoading
  } = useNotifications();

  // Fetch page configuration from the new endpoint
  const fetchPageConfig = usePost('/api/admin_panel/page_config');

  const { data: pageConfigResponse, isLoading: isConfigLoading } = useQuery({
    queryKey: ['adminPanelPageConfig', 'Notifications Overview'],
    queryFn: async () => {
      const response = await fetchPageConfig({
        kind: 'Notifications Overview'
      });
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  });

  // Extract config from response
  const pageConfig = pageConfigResponse?.data?.config;

  // Count notifications with SQL errors
  const notificationsWithErrors = notifications.filter(notification =>
    notification.sqlError || (notification.expandableRows?.some(row => row.sqlError))
  ).length;

  // Use config to determine what to show
  const showEnabledNotifications = pageConfig?.summaryMetrics?.enabledNotifications === 'visible';
  const showSqlErrors = pageConfig?.summaryMetrics?.sqlErrors === 'visible';
  const pageTitle = pageConfig?.pageHeader?.title?.[context.lang] || pageConfig?.pageHeader?.title?.['en'] || L.admin_panel.nav.notifications;
  const pageDescription = pageConfig?.pageHeader?.description?.[context.lang] || pageConfig?.pageHeader?.description?.['en'] || L.admin_panel.notifications.description;

  const enabledNotificationsCard = showEnabledNotifications ? (
    <NumberDisplay
      loading={isNotificationsLoading}
      className="sm:max-w-64 min-w-64"
      title={L.admin_panel.notifications.enabled_notifications}
      value={enabledNotifications}
      iconName="BellIcon"
      iconColor="blue"
    />
  ) : null;

  const sqlErrorsCard = notificationsWithErrors > 0 && showSqlErrors ? (
    <NumberDisplay
      loading={isNotificationsLoading}
      className="sm:max-w-64 min-w-64"
      title={L.admin_panel.notifications.sql_errors}
      value={notificationsWithErrors}
      iconName="ExclamationTriangleIcon"
      iconColor="red"
    />
  ) : null;

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: pageTitle,
    }
  ];

  return (
    <AdminPanelPage
      title={pageTitle}
      description={pageDescription}
      breadcrumbs={breadcrumbs}
      pageActionButtons={[
        <Tooltip text={L.admin_panel.payments.refresh_data} asChild>
          <Button
            size="sm"
            variant='secondary'
            onClick={() => { fetchNotificationsOverview(true) }}
            icon={<Icon role="decorative" type="solid" name="ArrowPathIcon" />}
            aria-label={L.admin_panel.payments.refresh_data}
          />
        </Tooltip>
      ]}
    >
      <div className="flex flex-col gap-2 sm:gap-4 w-full">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          {enabledNotificationsCard}
          {sqlErrorsCard}
        </div>
        <div>
          <NotificationsDataTable
            lastUpdatedTimestamp={notificationsDataTimestamp}
            pageConfig={pageConfig}
            isConfigLoading={isConfigLoading}
          />
        </div>
      </div>
    </AdminPanelPage>
  )
}
